import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Grid,
  Rating,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  InputAdornment,
  IconButton,
  Paper,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile, getUserProfile, deleteUserProfile } from '../../firebase/userService';
import { UserProfile, Language } from '../../types/user';
import LanguageIcon from '@mui/icons-material/Language';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import StarIcon from '@mui/icons-material/Star';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import { doc, getDoc, updateDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';

// Utilisation de type any pour éviter les conflits avec les types existants
// Cette approche est temporaire jusqu'à l'installation complète des types Stripe

const languages: { code: Language; label: string }[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
];

const timezones = Intl.supportedValuesOf('timeZone');

const ProfileCard = ({ 
  title, 
  icon, 
  children, 
  elevation = 3,
  isEditing = false
}: { 
  title: string; 
  icon: React.ReactNode; 
  children: React.ReactNode; 
  elevation?: number;
  isEditing?: boolean;
}) => (
  <Card
    elevation={elevation}
    sx={{
      p: 3,
      height: '100%',
      background: 'linear-gradient(145deg, rgba(45, 46, 73, 0.7) 0%, rgba(35, 36, 63, 0.9) 100%)',
      borderRadius: '40px',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      position: 'relative',
      overflow: 'hidden',
      opacity: isEditing ? 1 : 0.55,
      filter: isEditing ? 'none' : 'grayscale(20%)',
      transition: 'all 0.3s ease',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(104, 241, 209, 0.4), transparent)',
      },
      '&::after': {
        content: isEditing ? 'none' : '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.1)',
        pointerEvents: 'none',
      }
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
      <Box sx={{ 
        color: '#68f1d1',
        display: 'flex',
        alignItems: 'center',
        opacity: 0.9,
      }}>
        {icon}
      </Box>
      <Typography 
        variant="h6" 
        sx={{ 
          color: '#68f1d1',
          fontSize: '1.1rem',
          fontWeight: 500,
          opacity: 0.9,
        }}
      >
        {title}
      </Typography>
    </Box>
    {children}
  </Card>
);

export default function ProfileManager() {
  const { userProfile, isAdmin, deleteAccount } = useAuth();
  const { t, i18n } = useTranslation();
  const [componentKey, setComponentKey] = useState(0);
  const [localProfile, setLocalProfile] = useState<UserProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState<UserProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [reviewStatus, setReviewStatus] = useState<{ type: 'success' | 'error' } | null>(null);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [correspondenceEmail, setCorrespondenceEmail] = useState('');
  const timezoneListRef = useRef<HTMLDivElement>(null);
  const selectedTimezoneRef = useRef<HTMLButtonElement>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  
  // Récupérer les paramètres d'URL
  const location = useLocation();
  
  // Vérifier si le modal doit être ouvert automatiquement
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    console.log('URL params:', location.search);
    console.log('pricing param:', params.get('pricing'));
    
    if (params.get('openPlanModal') === 'true' || params.get('openPricing') === 'true' || params.get('pricing') === '1') {
      console.log('Ouverture du modal de pricing');
      setTimeout(() => {
        setShowPlanModal(true);
      }, 100); // Petit délai pour s'assurer que le composant est bien monté
      
      // Nettoyer l'URL pour éviter de rouvrir le modal si l'utilisateur rafraîchit la page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location]);

  // Force le re-render quand la langue change
  useEffect(() => {
    // Mise à jour des profils avec la nouvelle langue
    if (localProfile) {
      const updatedProfile = { ...localProfile, language: i18n.language as Language };
      setLocalProfile(updatedProfile);
      if (editedProfile) {
        setEditedProfile({ ...editedProfile, language: i18n.language as Language });
      }

      // Attendre que le composant soit mis à jour
      setTimeout(() => {
        if (selectedTimezoneRef.current && timezoneListRef.current) {
          const container = timezoneListRef.current;
          const selected = selectedTimezoneRef.current;
          
          container.scrollTop = selected.offsetTop - container.offsetHeight / 2 + selected.offsetHeight / 2;
        }
      }, 0);
    }
    setComponentKey(prev => prev + 1);
  }, [i18n.language]);

  // Fonction pour formater la date selon la langue
  const formatDate = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    };
    
    return date.toLocaleDateString(i18n.language, options);
  };

  // Initialiser localProfile avec userProfile
  useEffect(() => {
    if (userProfile) {
      setLocalProfile(userProfile);
      setEditedProfile(userProfile);
    }
  }, [userProfile]);

  // Charger l'appréciation existante
  useEffect(() => {
    if (!userProfile?.uid) return;
    
    const loadReview = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', userProfile.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.rating) setRating(userData.rating);
          if (userData.review) setComment(userData.review);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'appréciation:', error);
      }
    };
    
    loadReview();
  }, [userProfile?.uid]);

  // Charger l'email de correspondance
  useEffect(() => {
    const loadEmail = async () => {
      try {
        const emailDoc = await getDoc(doc(db, 'settings', 'email'));
        if (emailDoc.exists()) {
          const email = emailDoc.data().correspondenceEmail || '';
          console.log('Email chargé:', email);
          setCorrespondenceEmail(email);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'email de correspondance:', error);
      }
    };
    loadEmail();
  }, []);

  // Gérer la soumission de l'appréciation
  const handleReviewSubmit = async () => {
    if (!rating || !comment.trim()) {
      setReviewStatus({ type: 'error' });
      return;
    }

    setSubmittingReview(true);

    try {
      // Logique d'envoi de l'avis
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Réinitialiser les champs après l'envoi
      setRating(null);
      setComment('');
      setReviewStatus({ type: 'success' });
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'avis:', error);
      setReviewStatus({ type: 'error' });
    } finally {
      setSubmittingReview(false);
    }
  };

  // Effet pour synchroniser avec Firestore
  useEffect(() => {
    const loadProfile = async () => {
      if (userProfile?.uid) {
        const profile = await getUserProfile(userProfile.uid);
        if (profile) {
          setLocalProfile(profile);
          setEditedProfile(profile);
        }
      }
    };
    loadProfile();
  }, [userProfile?.uid]);

  // Effet pour scroller jusqu'au fuseau horaire sélectionné
  useEffect(() => {
    if (timezoneListRef.current && selectedTimezoneRef.current) {
      const list = timezoneListRef.current;
      const selected = selectedTimezoneRef.current;
      const selectedTop = selected.offsetTop;
      const listHeight = list.clientHeight;
      
      // Centre l'élément sélectionné dans la vue
      list.scrollTop = selectedTop - (listHeight / 2) + (selected.clientHeight / 2);
    }
  }, [localProfile?.timezone, editedProfile?.timezone]);

  const handleEdit = () => {
    setIsEditing(true);
    // Copie les valeurs actuelles dans editedProfile
    if (localProfile) {
      setEditedProfile({ ...localProfile });
    }
  };

  const handleSave = async () => {
    if (userProfile?.uid && editedProfile) {
      try {
        const updates = {
          username: editedProfile.username,
          language: editedProfile.language,
          timezone: editedProfile.timezone,
        };
        
        // Sauvegarde dans Firebase
        await updateUserProfile(userProfile.uid, updates);
        
        // Recharge le profil depuis Firestore
        const updatedProfile = await getUserProfile(userProfile.uid);
        if (updatedProfile) {
          setLocalProfile(updatedProfile);
          setEditedProfile(updatedProfile);
        }
        
        setIsEditing(false);
      } catch (error) {
        console.error("Erreur lors de la mise à jour du profil:", error);
      }
    }
  };

  const handleCancel = () => {
    if (localProfile) {
      setEditedProfile(localProfile);
    }
    setIsEditing(false);
  };

  const getPlanLabel = (plan: string) => {
    switch (plan) {
      case 'premium':
        return t('profile.plan.premium');
      case 'free':
        return t('profile.plan.free');
      default:
        return plan.charAt(0).toUpperCase() + plan.slice(1);
    }
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      color: 'rgba(55, 255, 255, 0.9)',
      backgroundColor: 'rgba(235, 206, 263, 0.3)',
      borderRadius: '20px',
      boxShadow: '0 4px 25px rgba(10, 10, 10, 0.8)',
      '& fieldset': {
        borderColor: 'rgba(104, 241, 209, 0.1)',
        borderWidth: '0px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(104, 241, 209, 0.3)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'rgba(104, 241, 209, 0.5)',
      },
      '&.Mui-disabled': {
        '& input': {
          color: '#68f1d1 !important',
          '-webkit-text-fill-color': '#68f1d1 !important',
          opacity: '1 !important',
        },
        '& .MuiOutlinedInput-input': {
          color: 'black !important',
          '-webkit-text-fill-color': 'black !important',
          opacity: '1 !important',
        }
      },
    },
    '& .MuiInputLabel-root': {
      color: '#ffb74d',
      '&.Mui-focused': {
        color: '#ffb74d',
      },
      '&.Mui-disabled': {
        color: '#ffb74d',
        opacity: 0.9,
      },
    },
    '& .MuiSelect-icon': {
      color: '#ffb74d',
    },
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError(t('profile.deleteConfirmation.passwordRequired'));
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      console.log("Tentative de suppression du compte...");
      await deleteAccount(deletePassword);
      // La redirection sera automatique car le contexte d'authentification
      // détectera la déconnexion et redirigera vers la page de connexion
      console.log("Suppression du compte réussie");
    } catch (error) {
      console.error("Erreur lors de la suppression du compte:", error);
      
      // Afficher un message d'erreur spécifique selon le type d'erreur
      if (error instanceof Error) {
        const errorMessage = error.message;
        console.log("Message d'erreur:", errorMessage);
        
        if (errorMessage.includes('auth/wrong-password')) {
          setDeleteError(t('profile.deleteConfirmation.wrongPassword'));
        } else if (errorMessage.includes('auth/requires-recent-login')) {
          setDeleteError(t('profile.deleteConfirmation.sessionExpired'));
        } else if (errorMessage.includes('insufficient permissions')) {
          setDeleteError(t('profile.deleteConfirmation.permissionError'));
        } else {
          setDeleteError(`${t('profile.deleteConfirmation.error')} (${errorMessage})`);
        }
      } else {
        setDeleteError(t('profile.deleteConfirmation.error'));
      }
      
      setIsDeleting(false);
    }
  };

  const handleCancelSubscription = async () => {
    console.log("🔍 Début handleCancelSubscription");
    try {
      setIsLoading(true);
      console.log("🔍 État isLoading défini à true");
      
      if (!userProfile) {
        console.error("❌ Erreur: Utilisateur non connecté");
        throw new Error('Utilisateur non connecté');
      }
      
      console.log("🔍 userProfile:", userProfile);
      
      // Vérification préalable - si l'utilisateur n'a pas d'abonnement actif ou de subscriptionId
      if (!userProfile.subscriptionId || userProfile.subscriptionStatus === 'canceled') {
        console.log("ℹ️ Aucun abonnement actif à annuler, mise à jour du profil uniquement");
        
        // Mise à jour Firestore directement
        try {
          const userRef = doc(db, 'users', userProfile.uid);
          await updateDoc(userRef, {
            plan: 'free',
            subscriptionStatus: 'canceled'
          });
          
          // Mettre à jour le profil local
          if (localProfile) {
            console.log("🔍 Mise à jour du profil local");
            setLocalProfile({
              ...localProfile,
              plan: 'free',
              subscriptionStatus: 'canceled'
            });
          }
          
          // Fermer le modal et afficher un message de succès
          setShowPlanModal(false);
          toast.success(t('profile.changePlan.cancelSuccess'));
          setIsLoading(false);
          return;
        } catch (dbError) {
          console.error("❌ Erreur lors de la mise à jour Firestore:", dbError);
          // Continuer avec l'appel API normal en cas d'échec
        }
      }
      
      console.log(`🔍 Envoi requête à ${import.meta.env.VITE_API_URL}/cancel-subscription`);
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userProfile.uid
        }),
      });
      
      console.log("🔍 Réponse reçue:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erreur lors de l\'annulation:', errorText);
        
        // Gestion spécifique de l'erreur "No such subscription"
        if (errorText.includes('No such subscription')) {
          console.log("ℹ️ L'abonnement n'existe pas sur Stripe, mise à jour du profil uniquement");
          
          // Mise à jour Firestore
          const userRef = doc(db, 'users', userProfile.uid);
          await updateDoc(userRef, {
            plan: 'free',
            subscriptionStatus: 'canceled'
          });
          
          // Mettre à jour le profil local
          if (localProfile) {
            console.log("🔍 Mise à jour du profil local");
            setLocalProfile({
              ...localProfile,
              plan: 'free',
              subscriptionStatus: 'canceled'
            });
          }
          
          // Fermer le modal et afficher un message de succès
          setShowPlanModal(false);
          toast.success(t('profile.changePlan.cancelSuccess'));
          setIsLoading(false);
          return;
        }
        
        throw new Error('Erreur lors de l\'annulation de l\'abonnement');
      }
      
      // Mettre à jour le profil local
      if (localProfile) {
        console.log("🔍 Mise à jour du profil local");
        setLocalProfile({
          ...localProfile,
          plan: 'free',
          subscriptionStatus: 'canceled'
        });
      }
      
      // Fermer le modal
      console.log("🔍 Fermeture du modal");
      setShowPlanModal(false);
      
      // Afficher un message de succès
      console.log("🔍 Affichage du message de succès");
      toast.success(t('profile.changePlan.cancelSuccess'));
      
    } catch (error) {
      console.error("❌ Erreur complète:", error);
      
      if (error instanceof Error) {
        console.error("❌ Message d'erreur:", error.message);
        toast.error(t('profile.changePlan.error'));
      }
    } finally {
      console.log("🔍 Fin handleCancelSubscription, isLoading défini à false");
      setIsLoading(false);
    }
  };

  const handleSubscription = async (priceId: string) => {
    console.log("🔍 Début handleSubscription avec priceId:", priceId);
    try {
      setIsLoading(true);
      console.log("🔍 État isLoading défini à true");
      
      if (!userProfile) {
        console.error("❌ Erreur: Utilisateur non connecté");
        throw new Error('Utilisateur non connecté');
      }
      
      console.log("🔍 userProfile:", userProfile);
      
      // Charger Stripe
      console.log("🔍 Chargement de Stripe avec clé:", import.meta.env.VITE_STRIPE_PUBLIC_KEY ? "Clé définie" : "Clé non définie");
      
      // Utiliser window.Stripe au lieu de loadStripe
      if (!(window as any).Stripe) {
        console.error("❌ Erreur: Stripe n'est pas disponible globalement");
        throw new Error('Stripe non disponible');
      }
      
      const stripe = (window as any).Stripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '');
      
      console.log("🔍 Stripe chargé avec succès");
      
      // Créer la session de paiement
      const requestBody = {
        priceId,
        userId: userProfile.uid,
        userEmail: userProfile.email
      };
      
      console.log("🔍 Corps de la requête:", requestBody);
      
      // S'assurer que l'URL est correctement formatée avec https://
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const baseUrl = apiUrl.startsWith('http') ? apiUrl : `https://${apiUrl}`;
      
      console.log("🔍 API URL d'origine:", apiUrl);
      console.log("🔍 API URL formatée:", baseUrl);
      console.log(`🔍 Envoi requête à ${baseUrl}/create-checkout-session`);
      
      const response = await fetch(`${baseUrl}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      console.log("🔍 Réponse reçue:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erreur lors de la création de la session:', errorText);
        throw new Error('Erreur lors de la création de la session de paiement');
      }
      
      const sessionData = await response.json();
      console.log("🔍 Session data reçue:", sessionData);
      
      // Rediriger vers la page de paiement Stripe
      console.log("🔍 Redirection vers Stripe avec sessionId:", sessionData.sessionId);
      const { error } = await stripe.redirectToCheckout({ sessionId: sessionData.sessionId });
      
      if (error) {
        console.error("❌ Erreur Stripe:", error.message);
        throw error;
      }
      
    } catch (error) {
      console.error("❌ Erreur complète:", error);
      console.error("❌ Message d'erreur:", error instanceof Error ? error.message : 'Erreur inconnue');
      toast.error(t('profile.changePlan.error'));
      setIsLoading(false);
    }
  };

  if (!userProfile) return null;
  if (!localProfile) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Typography>{t('profile.loading')}</Typography>
    </Box>
  );

  return (
    <Box key={componentKey} sx={{ maxWidth: 800, mx: 'auto', p: 0 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        mt: 14,
        mb: 4,
      }}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          mb: 4
        }}>
          <Typography variant="h3" sx={{ 
            color: 'white',
            fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
            fontWeight: 700,
            letterSpacing: '0.5px',
            fontFamily: 'Comfortaa'          
          }}>
            {t('profile.title')}
          </Typography>
          <Typography variant="body1" sx={{ 
            color: '#68f1d1',
            opacity: 0.8
          }}>
            {t('profile.subtitle')}
          </Typography>
        </Box>

        {/* Informations du Compte */}
        <Box sx={{ 
          width: '100%', 
          mb: 4,
          p: 3,
          background: 'linear-gradient(145deg, rgba(45, 46, 73, 0.7) 0%, rgba(35, 36, 63, 0.9) 100%)',
          borderRadius: '40px',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 0px 23px rgba(0, 0, 0, 0.4)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(104, 241, 209, 0.4), transparent)',
          },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
            <Box sx={{ 
              color: '#68f1d1',
              display: 'flex',
              alignItems: 'center',
              opacity: 0.9,
            }}>
              <WorkspacePremiumIcon />
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#68f1d1',
                fontSize: '1.1rem',
                fontWeight: 500,
                opacity: 0.9,
              }}
            >
              {t('profile.sections.accountInfo')}
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 2,
            color: 'white',
          }}>
            <Typography sx={{ opacity: 0.8 }}>
              {t('profile.registration')} : {localProfile?.registrationDate ? formatDate(new Date(localProfile.registrationDate)) : t('profile.notAvailable')}
            </Typography>
            <Typography 
              sx={{ 
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF4B8C 100%)',
                borderRadius: '20px',
                px: 2,
                py: 1,
                display: 'inline-block',
                cursor: 'pointer',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FF8C42 0%, #FF4B8C 100%)',
                  boxShadow: '0 4px 40px rgba(255, 75, 140, 0.7)',
                },
              }}
              onClick={() => setShowPlanModal(true)}
            >
              {t('profile.plan.title')} : {localProfile?.plan ? localProfile.plan.charAt(0).toUpperCase() + localProfile.plan.slice(1) : 'Non disponible'}
            </Typography>
          </Box>
        </Box>

        {!isEditing ? (
          <Button
            onClick={handleEdit}
            sx={{
              background: 'linear-gradient(135deg, #68f1d1 0%, #e57373 130%)',
              color: '#1a1b35',
              borderRadius: '30px',
              px: 3,
              minWidth: '200px',
              boxShadow: '0 4px 20px rgba(10, 10, 10, 0.8)',
              '&:hover': {
                background: 'linear-gradient(135deg, #306165 0%, #68f1d1 100%)',
                boxShadow: '0 4px 25px rgba(10, 10, 10, 0.8)',
              },
            }}
          >
            {t('profile.editButton')}
          </Button>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexDirection: { xs: 'column', sm: 'row' },
            width: '100%',
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            {/*LES TROIS BOUTONS DU MENU EDIT PROFIL */}
            <Button
              onClick={handleCancel}
              variant="outlined"
              sx={{
                borderColor: 'rgba(104, 241, 209, 0.3)',
                color: '#68f1d1',
                borderRadius: '30px',
                px: { xs: 2, sm: 3 },
                minWidth: { xs: 'auto', sm: '150px' },
                width: { xs: '100%', sm: 'auto' },
                boxShadow: '0 4px 20px rgba(10, 10, 10, 0.8)',
                '&:hover': {
                  color: 'black',
                  borderColor: 'rgba(104, 241, 209, 0.5)',
                  backgroundColor: 'rgba(104, 241, 209, 0.9)',
                  boxShadow: '0 4px 25px rgba(10, 10, 10, 0.8)',
                },
              }}
            >
              {t('profile.cancelButton')}
            </Button>
            <Button
              onClick={handleSave}
              sx={{
                background: 'linear-gradient(135deg, #68f1d1 0%, #4ac9ab 100%)',
                color: '#1a1b35',
                borderRadius: '30px',
                px: { xs: 2, sm: 3 },
                minWidth: { xs: 'auto', sm: '150px' },
                width: { xs: '100%', sm: 'auto' },
                boxShadow: '0 8px 25px rgba(10, 10, 10, 0.9)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4ac9ab 0%, #68f1d1 100%)',
                  boxShadow: '0 8px 25px rgba(10, 10, 10, 0.9)',
                },
              }}
            >
              {t('profile.saveButton')}
            </Button>
            <Button
              onClick={() => setDeleteConfirmation(true)}
              sx={{
                background: 'linear-gradient(135deg, #FF3737 0%, #FF8C42 100%)',
                color: '#1a1b35',
                borderRadius: '30px',
                px: { xs: 2, sm: 3 },
                minWidth: { xs: 'auto', sm: '150px' },
                width: { xs: '100%', sm: 'auto' },
                boxShadow: '0 8px 25px rgba(10, 10, 10, 0.9)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FF8C42 0%, #FF3737 100%)',
                  boxShadow: '0 8px 25px rgba(10, 10, 10, 0.9)',
                },
              }}
            >
              {t('profile.deleteButton')}
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>


        {/* Informations de base */}
        <Grid item xs={12} md={6}>
          <ProfileCard title={t('profile.sections.username')} icon={<EmailIcon />} isEditing={isEditing}>
            <TextField
              value={isEditing && editedProfile ? editedProfile.username : (localProfile?.username || '')}
              onChange={(e) => editedProfile && setEditedProfile({ ...editedProfile, username: e.target.value })}
              disabled={!isEditing}
              fullWidth
              sx={inputStyles}
            />
          </ProfileCard>
        </Grid>

        {/* Email */}
        <Grid item xs={12} md={6}>
          <ProfileCard title={t('profile.sections.contact')} icon={<EmailIcon />} isEditing={isEditing}>
            <TextField
              value={userProfile?.email || ''}
              disabled
              fullWidth
              sx={inputStyles}
            />
          </ProfileCard>
        </Grid>

        {/* Langue */}
        <Grid item xs={12} md={6}>
          <ProfileCard title={t('profile.sections.language')} icon={<LanguageIcon />} isEditing={isEditing}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 1
            }}>
              {languages.map((lang) => (
                <Button
                  key={lang.code}
                  onClick={() => isEditing && editedProfile && setEditedProfile({ ...editedProfile, language: lang.code })}
                  sx={{
                    background: (isEditing && editedProfile ? editedProfile.language : localProfile?.language) === lang.code 
                      ? 'linear-gradient(135deg, #68f1d1 0%, #FF4B8C 130%)'
                      : 'linear-gradient(135deg, rgba(30, 30, 30, 0.4) 0%, rgba(50, 50, 50, 0.4) 100%)',
                    color: (isEditing && editedProfile ? editedProfile.language : localProfile?.language) === lang.code 
                      ? '#1a1b35'
                      : '#68f1d1',
                    justifyContent: 'flex-start',
                    px: 2,
                    py: 1,
                    borderRadius: '20px',
                    opacity: (isEditing && editedProfile ? editedProfile.language : localProfile?.language) === lang.code 
                      ? 1 
                      : (!isEditing ? 0.7 : 1),
                    '&:hover': {
                      background: isEditing 
                        ? ((isEditing && editedProfile ? editedProfile.language : localProfile?.language) === lang.code
                          ? 'linear-gradient(135deg, #68f1d1 0%, #FF4B8C 130%)'
                          : 'linear-gradient(135deg, rgba(40, 40, 40, 0.6) 0%, rgba(60, 60, 60, 0.6) 100%)')
                        : ((isEditing && editedProfile ? editedProfile.language : localProfile?.language) === lang.code
                          ? 'linear-gradient(135deg, #68f1d1 0%, #FF4B8C 130%)'
                          : 'linear-gradient(135deg, rgba(30, 30, 30, 0.4) 0%, rgba(50, 50, 50, 0.4) 100%)'),
                    },
                    cursor: isEditing ? 'pointer' : 'default',
                  }}
                >
                  {lang.label}
                </Button>
              ))}
            </Box>
          </ProfileCard>
        </Grid>

        {/* Fuseau horaire */}
        <Grid item xs={12} md={6}>
          <ProfileCard title={t('profile.sections.timezone')} icon={<AccessTimeIcon />} isEditing={isEditing}>
            <Box 
              ref={timezoneListRef}
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 1,
                maxHeight: '200px',
                overflowY: 'auto',
                scrollBehavior: 'smooth',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(104, 241, 209, 0.3)',
                  borderRadius: '4px',
                  '&:hover': {
                    background: 'rgba(104, 241, 209, 0.5)',
                  },
                },
              }}
            >
              {timezones.map((zone) => (
                <Button
                  key={zone}
                  ref={zone === (isEditing && editedProfile ? editedProfile.timezone : localProfile?.timezone) ? selectedTimezoneRef : null}
                  onClick={() => isEditing && editedProfile && setEditedProfile({ ...editedProfile, timezone: zone })}
                  sx={{
                    background: (isEditing && editedProfile ? editedProfile.timezone : localProfile?.timezone) === zone 
                      ? 'linear-gradient(135deg, #68f1d1 0%, #FF4B8C 130%)'
                      : 'linear-gradient(135deg, rgba(30, 30, 30, 0.4) 0%, rgba(50, 50, 50, 0.4) 100%)',
                    color: (isEditing && editedProfile ? editedProfile.timezone : localProfile?.timezone) === zone 
                      ? '#1a1b35'
                      : '#68f1d1',
                    justifyContent: 'flex-start',
                    px: 2,
                    py: 1,
                    borderRadius: '20px',
                    opacity: (isEditing && editedProfile ? editedProfile.timezone : localProfile?.timezone) === zone 
                      ? 1 
                      : (!isEditing ? 0.7 : 1),
                    '&:hover': {
                      background: isEditing 
                        ? ((isEditing && editedProfile ? editedProfile.timezone : localProfile?.timezone) === zone
                          ? 'linear-gradient(135deg, #68f1d1 0%, #FF4B8C 130%)'
                          : 'linear-gradient(135deg, rgba(40, 40, 40, 0.6) 0%, rgba(60, 60, 60, 0.6) 100%)')
                        : ((isEditing && editedProfile ? editedProfile.timezone : localProfile?.timezone) === zone
                          ? 'linear-gradient(135deg, #68f1d1 0%, #FF4B8C 130%)'
                          : 'linear-gradient(135deg, rgba(30, 30, 30, 0.4) 0%, rgba(50, 50, 50, 0.4) 100%)'),
                    },
                    cursor: isEditing ? 'pointer' : 'default',
                  }}
                >
                  {zone}
                </Button>
              ))}
            </Box>
          </ProfileCard>
        </Grid>


        {/* Bouton Écrivez-nous */}
        {correspondenceEmail && (
          <Grid item xs={12} md={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(correspondenceEmail);
                  setCopySuccess(true);
                  setTimeout(() => setCopySuccess(false), 2000);
                } catch (err) {
                  console.error('Erreur lors de la copie:', err);
                }
              }}
              sx={{
                mb: 1,
                backgroundColor: '#68f1d1',
                color: '#1a1b2e',
                py: 2,
                fontSize: '1.1rem',
                '&:hover': {
                  backgroundColor: '#5fdfb4',
                },
              }}
            >
              {t('profile.contact.writeToUs')} : {correspondenceEmail}
            </Button>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                textAlign: 'center',
                color: copySuccess ? '#68f1d1' : 'text.secondary',
                transition: 'color 0.3s ease'
              }}
            >
              {copySuccess ? t('profile.contact.copied') : t('profile.contact.clickToCopy')}
            </Typography>
          </Grid>
        )}

        {/* Carte Appréciation */}
        <Grid item xs={12} md={6}>
          <ProfileCard
            title={t('profile.review.title')}
            icon={<StarIcon sx={{ color: '--light-green', fontSize: 24 }} />}
            isEditing={true} // Toujours éditable, indépendamment du mode d'édition
          >
            <Stack spacing={2}>
              <Rating
                value={rating}
                onChange={(_, newValue) => {
                  setRating(newValue);
                  setReviewStatus(null);
                }}
                size="large"
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: 'var(--star-icons)',
                  },
                }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder={t('profile.review.placeholder')}
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  setReviewStatus(null);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '15px',
                    '& fieldset': {
                      borderColor: '#68f1d1',
                      borderRadius: '15px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#5fdfb4',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#68f1d1',
                    },
                  },
                }}
              />
              {reviewStatus && (
                <Typography
                  color={reviewStatus.type === 'success' ? '#68f1d1' : 'error'}
                  sx={{ mt: 1 }}
                >
                  {t(`profile.review.${reviewStatus.type === 'success' ? 'success' : 'error'}`)}
                </Typography>
              )}
              <Button
                fullWidth
                variant="contained"
                onClick={handleReviewSubmit}
                disabled={submittingReview}
                sx={{
                  backgroundColor: '#68f1d1',
                  color: '#1a1b2e',
                  '&:hover': {
                    backgroundColor: '#5fdfb4',
                  },
                  '&.Mui-disabled': {
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              >
                {submittingReview ? t('profile.review.sending') : t('profile.review.submitButton')}
              </Button>
            </Stack>
          </ProfileCard>
        </Grid>
      </Grid>
      
      {/* Modal de changement de plan */}
      <Dialog
        open={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '95%',
            maxWidth: { xs: '100%', sm: '600px', md: '900px' },
            margin: { xs: 0, sm: '32px' },
            position: 'relative'
          }
        }}
        PaperProps={{
          sx: {
            background: 'var(--Pages-background)',
            borderRadius: '20px',
            p: 0,
            maxWidth: "md",
            overflow: 'visible'
          }
        }}
      >
        <IconButton
          aria-label="close"
          onClick={() => setShowPlanModal(false)}
          sx={{
            position: 'absolute',
            right: '15px',
            top: '15px',
            color: 'white',
            background: 'var(--card-gradient)',
            width: '32px',
            height: '32px',
            zIndex: 1,
            borderRadius: '50%',
            boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
            '&:hover': {
              background: 'linear-gradient(90deg, #FF4B8C 10%, #68f1d1 150%)',
              color: 'white',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ color: 'white', textAlign: 'center', pt: 4 }}>
          {t('profile.changePlan.title')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'white', mb: 0, textAlign: 'center' }}>
            {t('profile.changePlan.description')}
          </Typography>
          <Typography sx={{ color: 'var(--light-green)', mb: 3, textAlign: 'center', fontSize: '0.9rem', fontStyle: 'italic' }}>
            {t('profile.changePlan.infoText')}
          </Typography>
          
          <Grid container spacing={3}>

            {/* Plan FREE ------------------------------------------------------------*/}
            <Grid item xs={12} md={4}>
              <Paper sx={{
                borderRadius: '25px',
                p: 3,
                height: '100%',
                background: localProfile?.plan === 'free' ? 'var(--Pages-background5)' : 'var(--Pages-background1)',
                color: 'white',
                border: localProfile?.plan === 'free' ? '2px solid #68f1d1' : 'none',
                position: 'relative',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  boxShadow: '0 0px 23px rgba(0, 0, 0, 0.95)',
                }
              }}>
                <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
                  {t('home.pricing.free.title')}
                </Typography>
                <Typography variant="h2" sx={{ mb: 3, color: '#68f1d1' }}>
                  0€
                  <Typography component="span" variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {t('home.pricing.perMonth')}
                  </Typography>
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.free.feature1')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.free.feature2')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.free.feature3')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.free.feature4')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.free.feature5')}</Typography>
                    <Typography sx={{ mb: 3 }}>✓ {t('home.pricing.features.free.feature6')}</Typography>
                </Box>
                <Button 
                  fullWidth 
                  onClick={() => localProfile?.plan !== 'free' && setShowCancelConfirmation(true)}
                  disabled={localProfile?.plan === 'free'}
                  sx={{
                    background: 'var(--card-gradient)',
                    color: 'white',
                    padding: '12px 30px',
                    borderRadius: '25px',
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 8px 15px rgba(0, 0, 0, 0.4)',
                    fontSize: '1rem',
                    '&:hover': {
                      color: 'black',
                      background: 'rgba(255,255,255,0.9)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'rgba(255, 255, 255, 0.5)'
                    }
                  }}
                >
                  {localProfile?.plan === 'free' ? t('profile.changePlan.currentPlan') : t('profile.changePlan.downgradeToFree')}
                </Button>
              </Paper>
            </Grid>

            {/* Plan BASIC ------------------------------------------------------------*/}
            <Grid item xs={12} md={4}>
              <Paper sx={{
                borderRadius: '25px',
                p: 3,
                height: '100%',
                background: localProfile?.plan === 'basic' ? 'var(--Pages-background5)' : 'var(--Pages-background1)',
                color: 'white',
                border: localProfile?.plan === 'basic' ? '2px solid #68f1d1' : 'none',
                position: 'relative',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  boxShadow: '0 0px 23px rgba(0, 0, 0, 0.95)',
                }
              }}>
                <Typography variant="h4" sx={{ mb: 1, fontWeight: 700}}>
                  {t('home.pricing.basic.title')}
                </Typography>
                <Typography variant="h2" sx={{ mb: 2, color: 'var(--light-green)' }}>
                  5€
                  <Typography component="span" variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {t('home.pricing.perMonth')}
                  </Typography>
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.basic.feature1')}</Typography>
                  <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.basic.feature2')}</Typography>
                  <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.basic.feature3')}</Typography>
                  <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.basic.feature4')}</Typography>
                  <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.basic.feature5')}</Typography>
                  <Typography sx={{ mb: 3 }}>✓ {t('home.pricing.features.basic.feature6')}</Typography>
                </Box>
                <Button 
                  fullWidth 
                  onClick={() => handleSubscription('price_1ThSiHHxwALmXf4xJUzGzHZE')}
                  disabled={localProfile?.plan === 'basic' || isLoading}
                  sx={{
                    background: 'var(--card-gradient)',
                    color: 'white',
                    padding: '12px 30px',
                    borderRadius: '25px',
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 8px 15px rgba(0, 0, 0, 0.4)',
                    fontSize: '1rem',
                    '&:hover': {
                      color: 'black',
                      background: 'rgba(255,255,255,0.9)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'rgba(255, 255, 255, 0.5)'
                    }
                  }}
                >
                  {isLoading ? t('common.loading') : localProfile?.plan === 'basic' ? t('profile.changePlan.currentPlan') : t('profile.changePlan.switchToBasic')}
                </Button>
              </Paper>
            </Grid>

            {/* Plan PREMIUM ------------------------------------------------------------*/}
            <Grid item xs={12} md={4}>
              <Paper sx={{
                borderRadius: '25px',
                p: 3,
                height: '100%',
                background: localProfile?.plan === 'premium' ? 'var(--Pages-background5)' : 'var(--Pages-background1)',
                color: 'white',
                border: localProfile?.plan === 'premium' ? '2px solid #68f1d1' : 'none',
                position: 'relative',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  boxShadow: '0 0px 23px rgba(0, 0, 0, 0.95)',
                }
              }}>
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: -25,
                    right: '10%',
                    background: 'var(--star-iconsDeg)',                      
                    p: 1,
                    borderRadius: '25px',
                    color: 'black',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                  }}
                >
                  {t('home.pricing.mostPopular')}
                </Box>
                <Typography variant="h4" sx={{ mb: 1, color: 'white', fontWeight: 700 }}>
                  {t('home.pricing.premium.title')}
                </Typography>
                <Typography variant="h2" sx={{ mb: 0, color: 'var(--light-green)' }}>
                  10€
                  <Typography component="span" variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9) !important' }}>
                    {t('home.pricing.perMonth')}
                  </Typography>
                </Typography>
                <Typography variant="h2" sx={{ mb: 3, color: 'var(--light-green)' }}>
                  110€
                  <Typography component="span" variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9) !important' }}>
                    {t('home.pricing.perYear')}
                  </Typography>
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature1')}</Typography>
                  <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature2')}</Typography>
                  <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature3')}</Typography>
                  <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature4')}</Typography>
                  <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature5')}</Typography>
                  <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature6')}</Typography>
                  <Typography sx={{ mb: 3, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature7')}</Typography>
                </Box>
                <Stack spacing={2}>
                  <Button 
                    fullWidth 
                    onClick={() => handleSubscription('price_1ThSqeHxwALmXf4xuBSnhDcL')}
                    disabled={localProfile?.plan === 'premium' || isLoading}
                    sx={{
                      background: 'var(--card-gradient)',
                      color: 'white',
                      padding: '12px 30px',
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem',
                      '&:hover': {
                        color: 'black',
                        background: 'rgba(255,255,255,0.9)',
                      },
                      '&.Mui-disabled': {
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'rgba(255, 255, 255, 0.5)'
                      }
                    }}
                  >
                    {isLoading ? t('common.loading') : localProfile?.plan === 'premium' ? t('profile.changePlan.currentPlan') : t('profile.changePlan.monthlyPremium')}
                  </Button>
                  
                  <Button 
                    fullWidth 
                    onClick={() => handleSubscription('price_1ThSsRHxwALmXf4x3yyg0nbr')}
                    disabled={localProfile?.plan === 'premium' || isLoading}
                    sx={{
                      background: 'var(--card-gradient)',
                      color: 'white',
                      padding: '12px 30px',
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem',
                      '&:hover': {
                        color: 'black',
                        background: 'rgba(255,255,255,0.9)',
                      },
                      '&.Mui-disabled': {
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'rgba(255, 255, 255, 0.5)'
                      }
                    }}
                  >
                    {isLoading ? t('common.loading') : localProfile?.plan === 'premium' ? t('profile.changePlan.currentPlan') : t('profile.changePlan.yearlyPremium')}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

      </Dialog>

      {/* Dialog de confirmation d'annulation d'abonnement */}
      <Dialog
        open={showCancelConfirmation}
        onClose={() => setShowCancelConfirmation(false)}
        PaperProps={{
          sx: {
            background: 'var(--Pages-background)',
            borderRadius: '20px',
            p: 2
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', textAlign: 'center' }}>
          {t('profile.changePlan.confirmCancel')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'white', mb: 3, textAlign: 'center' }}>
            {t('profile.changePlan.confirmCancelText')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', p: 2 }}>
          <Button 
            onClick={() => setShowCancelConfirmation(false)}
            sx={{
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              padding: '10px 30px',
              borderRadius: '25px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={() => {
              setShowCancelConfirmation(false);
              handleCancelSubscription();
            }}
            sx={{
              background: 'linear-gradient(90deg, #FF4B8C 50%, #FF8E6E 100%)',
              color: 'white',
              padding: '10px 30px',
              borderRadius: '25px',
              '&:hover': {
                background: 'linear-gradient(90deg, #FF4B8C 10%, #68f1d1 150%)',
              }
            }}
          >
            {t('common.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de suppression */}    
      <Dialog 
        open={deleteConfirmation}
        onClose={() => {
          if (!isDeleting) {
            setDeleteConfirmation(false);
            setDeletePassword('');
            setDeleteError(null);
          }
        }}
        PaperProps={{
          style: {
            alignItems: 'center',
            background: 'linear-gradient(50deg, #240839 10%, #0ec7f1 180%)',
            borderRadius: '32px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'var(--light-green)', textAlign: 'center' }}>
          {t('profile.deleteConfirmation.title')}
        </DialogTitle>
        <Box sx={{
          backgroundColor: '#252942',
          borderRadius: '32px',
          width: '85%',
          p: 6,
          mb: 2,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.8)'
        }}>
        <DialogContent>
          <DialogContentText sx={{ color: 'var(--light-green)', textAlign: 'center', mb: 2 }}>
            {t('profile.deleteConfirmation.message')}
          </DialogContentText>
          <DialogContentText sx={{ mb: 2, fontWeight: 'bold', color: 'var(--primary-color)', textAlign: 'center' }}>
            {t('profile.deleteConfirmation.warning')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('profile.deleteConfirmation.passwordLabel')}
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            disabled={isDeleting}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ 
                      color: 'var(--light-green)',
                      '&:hover': {
                        color: 'var(--primary-color)',
                      }
                    }}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#FF3737',
                },
                '&:hover fieldset': {
                  borderColor: '#FF8C42',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#FF3737',
                },
              },
            }}
          />
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        </Box>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button 
            onClick={() => {
              setDeleteConfirmation(false);
              setDeletePassword('');
              setDeleteError(null);
            }} 
            sx={{
              color: 'var(--dark-blue)',
              background: 'var(--light-green)',
              mb: 2,
              mr: 2,
              '&:hover': {
                color: 'var(--light-green)',
                background: 'var(--dark-blue)',
                boxShadow: '0 8px 25px rgba(10, 10, 10, 0.9)',
              },     
            }}
            disabled={isDeleting}
          >
            {t('profile.deleteConfirmation.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            sx={{
              background: 'linear-gradient(135deg, #FF3737 0%, #FF8C42 100%)',
              color: '#1a1b35',
              borderRadius: '30px',
              px: 3,
              minWidth: '150px',
              boxShadow: '0 8px 25px rgba(10, 10, 10, 0.9)',
              mb: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF3737 100%)',
                boxShadow: '0 8px 25px rgba(10, 10, 10, 0.9)',
              },
            }}
            disabled={isDeleting}
          >
            {isDeleting ? t('profile.deleteConfirmation.deleting') : t('profile.deleteConfirmation.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
